import { Injectable, inject } from '@angular/core';
import { AppDialogService } from './app-dialog.service';

import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../shared/components/confirm-dialog/confirm-dialog.component';

@Injectable({ providedIn: 'root' })
export class AlertService {
  private readonly dialogService = inject(AppDialogService);
  private readonly messageService = inject(MessageService);

  /** Two-button confirmation dialog. Returns true if the user clicked Confirm. */
  async confirm(title: string, text = '', confirmLabel?: string, cancelLabel?: string): Promise<boolean> {
    // Dismissing reads as a cancel: onClose carries undefined, which is not true.
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      data: { title, message: text, icon: 'warning', confirmLabel, cancelLabel } satisfies ConfirmDialogData,
      width: '420px',
      focusOnShow: false,
    });
    return (await firstValueFrom(ref.onClose)) === true;
  }

  /** Pre-built delete confirmation. */
  async confirmDelete(itemName = ''): Promise<boolean> {
    return this.confirm(itemName ? `Supprimer ${itemName} ?` : 'Supprimer ?', 'Cette action est irréversible.');
  }

  /**
   * Single-button alert dialog (no cancel).
   * Pass disableClose=true to prevent Escape/backdrop dismiss.
   */
  async showAlert(
    icon: 'success' | 'error' | 'warning' | 'info',
    title: string,
    text = '',
    disableClose = false,
  ): Promise<void> {
    const config = {
      data: { title, message: text, icon, cancelLabel: null } satisfies ConfirmDialogData,
      width: '420px',
      focusOnShow: false,
    };
    const ref = disableClose
      ? this.dialogService.openBlocking(ConfirmDialogComponent, config)
      : this.dialogService.open(ConfirmDialogComponent, config);
    await firstValueFrom(ref.onClose);
  }

  /** Show a notification then immediately invoke the callback. */
  showAlertWithConfirmation(icon: 'success' | 'error', title: string, text: string, callback: () => void): void {
    this.toast(icon, title, text);
    callback();
  }

  /** Auto-closing toast. */
  showAlertWithDuration(icon: 'success' | 'error' | 'warning', title: string, text = ''): void {
    this.toast(icon, title, text);
  }

  /** Auto-closing success toast. */
  success(title: string, text = ''): void {
    this.toast('success', title, text);
  }

  /** Auto-closing error toast. */
  error(title: string, text = ''): void {
    this.toast('error', title, text);
  }

  /** Auto-closing neutral toast — for an outcome that is neither a success nor a problem. */
  info(title: string, text = ''): void {
    this.toast('info', title, text);
  }

  private toast(type: 'success' | 'error' | 'warning' | 'info', title: string, detail = ''): void {
    this.messageService.add({
      severity: type === 'warning' ? 'warn' : type,
      summary: title,
      detail,
      life: 3500,
    });
  }
}
